import * as React from "react"
import {Navigate} from "react-router-dom";
import {MaximalLayout} from "@app";

import {SESSION_ROUTES} from "@constants";
import {useUserApp} from "@hooks";
import {VersionBadge} from "@components";
import routes from "Routes";
import {appConfig, styles} from "./config";
import LoadingScreen from "./LoadingScreen";


export default function AppLayout() {
	const { userData, userEnv, userRole, isLogged, checkingLogging } = useUserApp(true)

	if (checkingLogging && !isLogged)
		return <LoadingScreen />

	if (!isLogged)
        return <Navigate to={SESSION_ROUTES.LOGIN_URL} replace />
	
	// access may be a single role or an array of roles
	const hasAccess = (access) => Array.isArray(access) ? access.includes(userRole) : access === userRole

	// A route may also declare which deployments it exists on. One bundle serves
	// both BUs, so this can only be decided at runtime: `env` travels with the
	// session ('dipi' on retail, 'puig' on puig). Checked BEFORE the role gate,
	// admin included — the backend answers 404 for these endpoints on the other
	// BU, so an admin following the menu entry would land on a broken screen.
	const isOnThisDeployment = (route) => !route?.deployments || route.deployments.includes(userEnv)

	const filteredRoutes = routes.map(route => {
		// If it's a group, filter its children
		if (route?.isGroup && route?.children) {
			const filteredChildren = route.children.filter(child => {
				if (!isOnThisDeployment(child)) return false
				// Admin sees everything
				if (userRole === 'admin') return true
				// If route has access restriction, show only if it matches user role
				if (child?.access) return hasAccess(child.access)
				// If no access restriction, hide it (all routes should have access defined now)
				return false
			})
			// Only return the group if it has visible children. A group left with a
			// SINGLE child is flattened to that child: the nav lib only renders a
			// collapsible group with >1 non-index children — a 1-child group became
			// a dead entry with no path (role 'user' + Retailers, 2026-07-28).
			if (filteredChildren.length === 0) return null
			if (filteredChildren.length === 1) return filteredChildren[0]
			return { ...route, children: filteredChildren }
		}
		// For non-group routes, apply the same filter
		if (!isOnThisDeployment(route)) return null
		// Admin sees everything
		if (userRole === 'admin') return route
		// If route has access restriction, show only if it matches user role
		if (route?.access) return hasAccess(route.access) ? route : null
		// If no access restriction, show it (for root redirect and session routes)
		return route
	}).filter(route => route !== null)

    return <>
		<MaximalLayout
			routes={filteredRoutes}
			config={appConfig}
			userData={userData}
			styles={styles}
		/>
		<VersionBadge />
	</>
}